/**
 * useVideoManagement Hook
 * 
 * Convenience hook for controlling video playback from any component level.
 * Wraps Redux actions for video management in an easy-to-use interface.
 * 
 * Usage examples:
 * 
 * 1. Pause active video when switching tabs:
 * ```tsx
 * const { pauseActiveVideo } = useVideoManagement();
 * 
 * const handleTabChange = (tab: string) => {
 *   if (tab !== 'videos') {
 *     pauseActiveVideo();
 *   }
 * };
 * ```
 * 
 * 2. Get current fullscreen state:
 * ```tsx
 * const { isFullscreen, fullscreenMedia } = useVideoManagement();
 * 
 * if (isFullscreen) {
 *   console.log('Video in fullscreen:', fullscreenMedia?.title);
 * }
 * ```
 * 
 * 3. Control specific video:
 * ```tsx
 * const { playVideo, pauseVideo } = useVideoManagement();
 * 
 * const handlePlaySpecificVideo = () => {
 *   playVideo('video-id-123');
 * };
 * ```
 */

import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '@/presentation/store';
import {
    requestPlay,
    requestPause,
    enterFullscreen,
    exitFullscreen,
    initVideo,
    setActiveVideo,
    requestBrowserFullscreen,
    requestVideoElementFullscreen,
    exitBrowserFullscreen,
    registerVideoElementState,
    unregisterVideoElementState,
} from '@/presentation/store/slices/videoManagement/videoManagementSlice';
import { clearVideo } from '@/presentation/store/slices/video/videoSlice';
import { videoElementRegistry } from '@/presentation/registries/videoElementRegistry';
import { useVideoPlayer } from '@/presentation/contexts/VideoPlayerContext';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';

// Read per call (not captured at module load) so a Remote Config activation that
// lands after startup takes effect on the next playback attempt.
// Flutter parity: these gates return silently — no overlay (video_player_notifier.dart:198-204).
const isPlaybackKillSwitched = () =>
    !remoteConfigService.initVideos || remoteConfigService.budgetExceeded;

export const useVideoManagement = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { destroyHlsInstance } = useVideoPlayer();

    // Selectors
    const activeVideoKey = useSelector((state: RootState) => state.videoManagement.activeVideoKey);
    const fullscreenState = useSelector((state: RootState) => state.videoManagement.fullscreen);
    const videoElements = useSelector((state: RootState) => state.videoManagement.videoElements);
    const playbackStates = useSelector((state: RootState) => state.videoManagement.playbackStates);

    /**
     * Internal helper to dispose a video with proper cleanup order
     * This ensures safe disposal: unmount → clear state → destroy resources
     */
    const disposeVideoInternal = (videoKey: string) => {
        const state = playbackStates[videoKey];
        if (!state) {
            // console.warn('⚠️ [useVideoManagement] No playback state for requested video');
            return;
        }

        console.log(`🗑️ [useVideoManagement] Disposing video ${videoKey}`);

        // 1. Mark as disposed (triggers VideoComponent unmount)
        dispatch(clearVideo(videoKey));

        // 2. Clear management state (after React starts unmounting)
        dispatch(unregisterVideoElementState({ videoKey }));

        // 3. Clean up external resources LAST (after component is unmounted)
        // Similar to Flutter: dispose controller after widget is unmounted
        setTimeout(() => {
            // Unregister from video element registry
            videoElementRegistry.unregister(videoKey);

            // Destroy HLS instance (frees memory: buffers, loaders, event listeners)
            destroyHlsInstance(videoKey);
        }, 100); // Small delay to ensure React unmount is complete
    };

    return {
        // State
        activeVideoKey,
        isFullscreen: fullscreenState.isFullscreen,
        fullscreenMedia: fullscreenState.media,
        fullscreenVideoKey: fullscreenState.videoElementKey,
        videoElements,
        playbackStates,

        // Actions
        /**
         * Initialize a video with decryption keys and optionally autoplay
         * This is the main entry point for video initialization
         * Optionally disposes the previous active video before initializing the new one
         */
        initializeVideo: async (media: UserMediaEntity, videoKey: string, autoplay = false, { disposePrevious = true } = {}) => {
            console.log(`🎬 [useVideoManagement] initializeVideo called. videoKey: ${videoKey}; activeVideoKey: ${activeVideoKey}`);
            if (disposePrevious && activeVideoKey && activeVideoKey !== videoKey) {
                console.log(`🗑️ [useVideoManagement] Disposing previous video`);
                disposeVideoInternal(activeVideoKey);
            }

            // Set active video key before initialization.
            // This ensures playback state is correctly set during init.
            // It also helps disposePrevious logic above.
            dispatch(setActiveVideo({ videoKey }));

            // Initialize the new video
            console.log(`🎬 [useVideoManagement] Initializing video ${videoKey}`);
            await dispatch(initVideo({ media, videoKey, autoplay }));

            registerVideoElementState({ videoKey });
        },        /**
         * Dispose a video and clean up all its resources
         * This will:
         * 1. Mark video as disposed (triggers React unmount)
         * 2. Clear playback state and unregister video element
         * 3. Destroy HLS instance (after unmount, prevents runtime errors)
         */
        disposeVideo: (videoKey: string) => {
            disposeVideoInternal(videoKey);
        },        /**
         * Play a specific video by key
         */
        playVideo: async (videoKey: string) => {
            // Kill-switches mirror Flutter video_player_notifier.dart:198-204.
            if (isPlaybackKillSwitched()) return;

            const state = playbackStates[videoKey];
            if (!state) {
                // console.warn('⚠️ [useVideoManagement] No playback state for requested video');
                return;
            }

            if (!state.isPlaying) {
                // Play and clear manual pause flag
                dispatch(requestPlay({ videoKey }));
                await videoElementRegistry.play(videoKey);
                // console.log('▶️ [useVideoManagement] Playing (manual)');
            }
        },

        /**
         * Pause a specific video by key
         * @param forced Whether to set forcedPaused flag (true = manual pause, false = auto pause)
         */
        pauseVideo: (videoKey: string, { forced = true }) => {
            const state = playbackStates[videoKey];
            if (!state) {
                // console.warn('⚠️ [useVideoManagement] No playback state for requested video');
                return;
            }

            if (state.isPlaying) {
                // Pause and set manual pause flag
                dispatch(requestPause({ videoKey, forced: forced }));
                videoElementRegistry.pause(videoKey);
                // console.log('⏸️ [useVideoManagement] Paused forced: ', forced);
            }
        },

        /**
         * Pause all videos
         */
        pauseAllVideos: () => {
            videoElementRegistry.pauseAll();
        },

        /**
         * Toggle play/pause for the active video (manual user interaction)
         * Sets pausedManually = true when pausing
         * Clears pausedManually = false when playing
         * Used by: VideoComponent, ContentListComponent
         */
        togglePlayPauseActiveVideo: async () => {
            // console.log('🎮 [useVideoManagement] togglePlayPauseActiveVideo called', { activeVideoKey });
            if (!activeVideoKey) {
                // console.warn('⚠️ [useVideoManagement] No active video to toggle');
                return;
            }

            const state = playbackStates[activeVideoKey];
            if (!state) {
                // console.warn('⚠️ [useVideoManagement] No playback state for active video');
                return;
            }

            if (state.isPlaying) {
                // Pause manually
                dispatch(requestPause({ videoKey: activeVideoKey, forced: true }));
                videoElementRegistry.pause(activeVideoKey);
                // console.log('⏸️ [useVideoManagement] Paused manually');
            } else {
                // Kill-switches mirror Flutter video_player_notifier.dart:198-204.
                if (isPlaybackKillSwitched()) return;

                // Play and clear manual pause flag
                dispatch(requestPlay({ videoKey: activeVideoKey }));
                await videoElementRegistry.play(activeVideoKey);
                // console.log('▶️ [useVideoManagement] Playing (manual)');
            }
        },

        /**
         * Replay the active video from the beginning.
         * Seeks to 0 and plays. Mirrors Flutter's `ContentViewModel.replayVideo`.
         */
        replayActiveVideo: async () => {
            if (!activeVideoKey) return;
            videoElementRegistry.seek(activeVideoKey, 0);
            const state = playbackStates[activeVideoKey];
            if (!state?.isPlaying) {
                dispatch(requestPlay({ videoKey: activeVideoKey }));
                await videoElementRegistry.play(activeVideoKey);
            }
        },

        /**
         * Skip the public preview part and jump to just before the exclusive
         * content starts. Mirrors Flutter's `ContentViewModel.skipPublicPart`
         * (seeks to `typeLimit - skipExpauseStartOffsetMilliseconds` then plays).
         */
        skipToExclusiveContent: async (media: UserMediaEntity) => {
            if (!activeVideoKey || media.typeLimit == null) return;
            // Matches Flutter DurationConstants.skipExpauseStartOffsetMilliseconds
            const SKIP_EXPAUSE_START_OFFSET_MS = 1000;
            const targetSec = Math.max(0, (media.typeLimit - SKIP_EXPAUSE_START_OFFSET_MS) / 1000);
            videoElementRegistry.seek(activeVideoKey, targetSec);
            dispatch(requestPlay({ videoKey: activeVideoKey }));
            await videoElementRegistry.play(activeVideoKey);
        },

        /**
         * Auto toggle play/pause for the active video (navigation events)
         * Respects pausedManually flag - won't auto-resume if manually paused
         * Used by: FeedPage navigation events (onPushNext, onPopNext, onPop)
         */
        autoTogglePlayPauseActiveVideo: async () => {
            // console.log('🤖 [useVideoManagement] autoTogglePlayPauseActiveVideo called', { activeVideoKey });
            if (!activeVideoKey) {
                // console.warn('⚠️ [useVideoManagement] No active video to auto-toggle');
                return;
            }

            const state = playbackStates[activeVideoKey];
            if (!state) {
                // console.warn('⚠️ [useVideoManagement] No playback state for active video');
                return;
            }

            if (state.isPlaying) {
                // Always pause on navigation away
                dispatch(requestPause({ videoKey: activeVideoKey, forced: false }));
                videoElementRegistry.pause(activeVideoKey);
                // console.log('⏸️ [useVideoManagement] Auto-paused (navigation)');
            } else if (!state.forcedPaused) {
                // Only auto-resume if NOT manually paused by user
                dispatch(requestPlay({ videoKey: activeVideoKey }));
                await videoElementRegistry.play(activeVideoKey);
                // console.log('▶️ [useVideoManagement] Auto-resumed (navigation)');
            } else {
                // console.log('⏭️ [useVideoManagement] Skipped auto-resume (forced paused)');
            }
        },

        /**
        * Get playback state for a specific video
        */
        getPlaybackState: (videoKey: string) => {
            return playbackStates[videoKey];
        },

        /**
         * Check if a specific video is currently playing
         */
        isVideoPlaying: (videoKey: string) => {
            return playbackStates[videoKey]?.isPlaying || false;
        },

        /**
         * Check if a specific video is the active video
         */
        isVideoActive: (videoKey: string) => {
            return activeVideoKey === videoKey;
        },

        /**
         * Seek to a specific time in the active video
         */
        seekActiveVideo: (time: number) => {
            if (activeVideoKey) {
                videoElementRegistry.seek(activeVideoKey, time);
            }
        },

        /**
         * Set volume for the active video
         */
        setActiveVideoVolume: (volume: number) => {
            if (activeVideoKey) {
                videoElementRegistry.setVolume(activeVideoKey, volume);
            }
        },

        /**
         * Set mute state for the active video
         */
        setActiveVideoMuted: (muted: boolean) => {
            if (activeVideoKey) {
                videoElementRegistry.setMuted(activeVideoKey, muted);
            }
        },

        /**
         * Toggle mute state for the active video
         */
        toggleActiveVideoMute: () => {
            if (activeVideoKey) {
                videoElementRegistry.toggleMute(activeVideoKey);
            }
        },

        /**
         * Enter fullscreen mode for a video
         * Must be called from synchronous click handler to preserve user gesture
         */
        /**
         * Enter native video element fullscreen (WEB_NATIVE_FULLSCREEN_MODE)
         * Must be called from synchronous click handler to preserve user gesture
         */
        enterVideoFullscreen: async (videoKey: string) => {
            await dispatch(requestVideoElementFullscreen({ videoKey })).unwrap();
        },

        /**
         * Enter fullscreen mode for a video
         * Must be called from synchronous click handler to preserve user gesture
         */
        enterFullscreen: async (media: UserMediaEntity, videoKey: string) => {
            // console.log('🎬 [useVideoManagement] enterFullscreen called', { videoKey });

            // Update Redux state first (this triggers overlay to render)
            dispatch(enterFullscreen({ media, videoKey }));
            // console.log('🎬 [useVideoManagement] Redux state updated, overlay should render');

            // Request browser fullscreen after React renders the overlay
            // Small delay to ensure overlay's useEffect has set the container
            // Still counts as part of the user gesture in browsers
            setTimeout(async () => {
                // console.log('🎬 [useVideoManagement] Requesting browser fullscreen (after 50ms delay)');
                try {
                    await dispatch(requestBrowserFullscreen()).unwrap();
                    // console.log('✅ [useVideoManagement] Browser fullscreen successful');
                } catch (error) {
                    // Browser fullscreen might fail, but our custom fullscreen still works
                    // console.warn('⚠️ [useVideoManagement] Browser fullscreen failed, using custom fullscreen only', error);
                }
            }, 0);
        },

        /**
         * Exit fullscreen mode
         */
        exitFullscreen: async () => {
            // console.log('🚪 [useVideoManagement] exitFullscreen called');

            // Exit browser fullscreen first
            await dispatch(exitBrowserFullscreen());
            // console.log('🚪 [useVideoManagement] Browser fullscreen exited');

            // Then update Redux state
            dispatch(exitFullscreen());
            // console.log('🚪 [useVideoManagement] Redux state cleared');
        },
    };
};
